import { uciToMove } from "./uci.js";

export const PERFORMANCE_ANALYSIS = Object.freeze({
  moveTimeMs: 180,
  nodeLimit: 180_000,
  skillLevel: 20,
  minThinkMs: 0,
  hashMb: 16,
  multiPv: 1,
  weakening: 0,
});

export class OpponentPolicy {
  constructor(engine, openingBooks, random) {
    this.engine = engine;
    this.openingBooks = openingBooks;
    this.random = random;
  }

  async chooseMove(session, opponent, difficulty) {
    const bookMove = findBookMove(
      session,
      this.openingBooks,
      opponent.openingStyleId,
    );
    if (bookMove) {
      return { move: bookMove, source: "opening_book", analysis: null };
    }
    const profile = deterministicProfile(difficulty);
    const analysis = await this.engine.analyze(session.fen(), profile);
    const moves = legalEngineMoves(session, analysis);
    if (!moves.length) {
      throw new Error("The engine returned no legal move.");
    }
    let selected = moves[0];
    if (moves.length > 1 && Number(difficulty.weakening) > 0) {
      if (this.random.next() < Number(difficulty.weakening)) {
        selected = this.random.pick(moves.slice(1));
      }
    }
    return { move: selected, source: "stockfish", analysis };
  }
}

export function deterministicProfile(difficulty) {
  return {
    ...difficulty,
    skillLevel: 20,
    nodeLimit: Math.max(
      1_000,
      Math.round(Number(difficulty.moveTimeMs) * 1_000),
    ),
  };
}

export function findBookMove(session, openingBooks, openingStyleId) {
  const book = openingBooks.find((item) => item.id === openingStyleId);
  if (!book) {
    return null;
  }
  const history = session
    .view()
    .history.map((move) => `${move.from}${move.to}${move.promotion ?? ""}`);
  const candidate = book.lines
    .filter((line) =>
      line
        .slice(0, history.length)
        .every((move, index) => move === history[index]),
    )
    .map((line) => line[history.length])
    .filter(Boolean)[0];
  const parsed = uciToMove(candidate);
  return parsed && moveIsLegal(session, parsed) ? parsed : null;
}

function legalEngineMoves(session, analysis) {
  const uciMoves = [
    ...(analysis.lines ?? []).map((line) => line.pv?.[0]),
    analysis.move,
  ];
  const unique = new Set();
  const result = [];
  for (const uci of uciMoves) {
    if (!uci || unique.has(uci)) {
      continue;
    }
    unique.add(uci);
    const move = uciToMove(uci);
    if (move && moveIsLegal(session, move)) {
      result.push(move);
    }
  }
  return result;
}

function moveIsLegal(session, move) {
  return session
    .legalMoves(move.from)
    .some(
      (legal) =>
        legal.to === move.to &&
        (!move.promotion || legal.promotion === move.promotion),
    );
}
